import React from 'react'
import axios from 'axios'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  IconButton,
  Checkbox,
  Typography,
  Grid,
  CircularProgress
} from '@material-ui/core'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon
} from '@material-ui/icons'

const emptyRate = { name: '', hourlyRate: '', includesOperator: false, notes: '' }

const EquipmentRatesTable = ({ url }) => {
  const [rates, setRates] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [editingId, setEditingId] = React.useState(null)
  const [editData, setEditData] = React.useState({})
  const [adding, setAdding] = React.useState(false)
  const [newRate, setNewRate] = React.useState({ ...emptyRate })

  React.useEffect(() => {
    fetchRates()
  }, [])

  const fetchRates = () => {
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/equipment-rates`).then(response => {
      setRates(response.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const handleEdit = (rate) => {
    setEditingId(rate._id)
    setEditData({
      name: rate.name || '',
      hourlyRate: rate.hourlyRate ?? '',
      includesOperator: !!rate.includesOperator,
      notes: rate.notes || ''
    })
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditData({})
  }

  const handleSaveEdit = () => {
    const payload = {
      ...editData,
      hourlyRate: Number(editData.hourlyRate)
    }
    axios.post(`${url}/api/bid-estimation/equipment-rates`, payload).then(() => {
      setEditingId(null)
      setEditData({})
      fetchRates()
    }).catch(err => {
      console.error(err)
    })
  }

  const handleAdd = () => {
    const payload = {
      ...newRate,
      hourlyRate: Number(newRate.hourlyRate)
    }
    axios.post(`${url}/api/bid-estimation/equipment-rates`, payload).then(() => {
      setAdding(false)
      setNewRate({ ...emptyRate })
      fetchRates()
    }).catch(err => {
      console.error(err)
    })
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <div>
      <Grid container justifyContent="flex-end" style={{ marginBottom: 16 }}>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
            disabled={adding}
          >
            Add Rate
          </Button>
        </Grid>
      </Grid>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell align="right">Hourly Rate ($)</TableCell>
            <TableCell align="center">Includes Operator</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {adding && (
            <TableRow>
              <TableCell>
                <TextField size="small" placeholder="Name" value={newRate.name} onChange={e => setNewRate({ ...newRate, name: e.target.value })} />
              </TableCell>
              <TableCell align="right">
                <TextField size="small" type="number" placeholder="Rate" value={newRate.hourlyRate} onChange={e => setNewRate({ ...newRate, hourlyRate: e.target.value })} style={{ width: 80 }} />
              </TableCell>
              <TableCell align="center">
                <Checkbox checked={newRate.includesOperator} onChange={e => setNewRate({ ...newRate, includesOperator: e.target.checked })} size="small" />
              </TableCell>
              <TableCell>
                <TextField size="small" placeholder="Notes" value={newRate.notes} onChange={e => setNewRate({ ...newRate, notes: e.target.value })} />
              </TableCell>
              <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                <IconButton size="small" onClick={handleAdd}><SaveIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => { setAdding(false); setNewRate({ ...emptyRate }) }}><CancelIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          )}
          {rates.length === 0 && !adding ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="body2" style={{ padding: 20 }}>
                  No equipment rates found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rates.map(rate => (
              <TableRow key={rate._id}>
                <TableCell>
                  {editingId === rate._id ? (
                    <TextField size="small" value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} />
                  ) : rate.name}
                </TableCell>
                <TableCell align="right">
                  {editingId === rate._id ? (
                    <TextField size="small" type="number" value={editData.hourlyRate} onChange={e => setEditData({ ...editData, hourlyRate: e.target.value })} style={{ width: 80 }} />
                  ) : (
                    `$${Number(rate.hourlyRate || 0).toFixed(2)}`
                  )}
                </TableCell>
                <TableCell align="center">
                  {editingId === rate._id ? (
                    <Checkbox checked={editData.includesOperator} onChange={e => setEditData({ ...editData, includesOperator: e.target.checked })} size="small" />
                  ) : (
                    <Checkbox checked={!!rate.includesOperator} disabled size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {editingId === rate._id ? (
                    <TextField size="small" value={editData.notes} onChange={e => setEditData({ ...editData, notes: e.target.value })} />
                  ) : rate.notes}
                </TableCell>
                <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                  {editingId === rate._id ? (
                    <>
                      <IconButton size="small" onClick={handleSaveEdit}><SaveIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={handleCancelEdit}><CancelIcon fontSize="small" /></IconButton>
                    </>
                  ) : (
                    <IconButton size="small" onClick={() => handleEdit(rate)}><EditIcon fontSize="small" /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export default EquipmentRatesTable
